import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { checkForNewMessages, getMessages } from "@/api/repositories/dialogs";
import { useDialogsStore } from "./dialogs";

const MESSAGES_PER_PAGE = 15;

export const useMessagesStore = defineStore(
  "messages",
  () => {
    const dialogsStore = useDialogsStore();

    // State
    const handlerInitialized = ref(false);
    const newMessage = ref(undefined);
    const cachedDialogsMessages = ref(new Map());
    const uncachedDialogsMessages = ref(new Map());
    const dialogsMessagesTimestamps = ref(new Map());

    // Actions
    function setNewMessage(newMsg) {
      newMessage.value = newMsg;

      if (
        newMsg?.dialog_id &&
        cachedDialogsMessages.value.has(newMsg.dialog_id)
      ) {
        addMessage(newMsg.dialog_id, newMsg);
      }
    }

    function setHandlerInitialized() {
      handlerInitialized.value = true;
    }

    async function fetchMessages(dialogId) {
      const timestamp = dialogsMessagesTimestamps.value.get(dialogId);
      const { hasNew } = timestamp
        ? await checkForNewMessages(dialogId, timestamp)
        : { hasNew: true };
      if (hasNew) {
        const { messages, hasMore } = await getMessages(dialogId);
        cachedDialogsMessages.value.set(dialogId, {
          messages,
        });
        uncachedDialogsMessages.value.set(dialogId, {
          messages: [],
          hasMore,
        });
        dialogsMessagesTimestamps.value.set(
          dialogId,
          Math.floor(Date.now() / 1000)
        );
      } else {
        uncachedDialogsMessages.value.set(dialogId, {
          messages: [],
          hasMore:
            cachedDialogsMessages.value.get(dialogId)?.messages?.length ===
            MESSAGES_PER_PAGE,
        });
      }
    }

    async function fetchMoreMessages(dialogId) {
      const currentMessages =
        uncachedDialogsMessages.value.get(dialogId)?.messages || [];
      const olderThan = currentMessages.length
        ? currentMessages.at(-1)?.created_at
        : cachedDialogsMessages.value.get(dialogId)?.messages?.at(-1)
            ?.created_at || Date.now();
      const { messages: newMessages, hasMore } = await getMessages(
        dialogId,
        olderThan
      );
      uncachedDialogsMessages.value.set(dialogId, {
        messages: [...currentMessages, ...newMessages],
        hasMore,
      });
    }

    function addMessage(dialogId, message) {
      if (!cachedDialogsMessages.value.has(dialogId)) return;

      const cachedMessages = cachedDialogsMessages.value.get(dialogId).messages;
      const existingIndex = cachedMessages.findIndex(
        (msg) => msg.id === message.id
      );

      if (existingIndex === -1) {
        cachedMessages.unshift(message);
        if (cachedMessages.length > MESSAGES_PER_PAGE) {
          const oldestMessage = cachedMessages.pop();
          const uncachedMessages =
            uncachedDialogsMessages.value.get(dialogId)?.messages || [];
          const hasMore =
            uncachedDialogsMessages.value.get(dialogId)?.hasMore || false;
          uncachedDialogsMessages.value.set(dialogId, {
            messages: [oldestMessage, ...uncachedMessages],
            hasMore,
          });
        }
      } else {
        cachedMessages[existingIndex] = message;
      }
      dialogsMessagesTimestamps.value.set(
        dialogId,
        Math.floor(Date.now() / 1000)
      );
    }

    function removeMessage(dialogId, messageId) {
      const cachedMessages =
        cachedDialogsMessages.value.get(dialogId)?.messages || [];
      const uncachedMessages =
        uncachedDialogsMessages.value.get(dialogId)?.messages || [];

      const cachedIndex = cachedMessages.findIndex(
        (msg) => msg.id === messageId
      );
      const uncachedIndex = uncachedMessages.findIndex(
        (msg) => msg.id === messageId
      );

      if (cachedIndex !== -1) {
        cachedMessages.splice(cachedIndex, 1);
      }

      if (uncachedIndex !== -1) {
        uncachedMessages.splice(uncachedIndex, 1);
      }

      uncachedDialogsMessages.value.set(dialogId, {
        messages: uncachedMessages,
        hasMore: uncachedDialogsMessages.value.get(dialogId)?.hasMore || false,
      });
    }

    function clearMessages(dialogId) {
      uncachedDialogsMessages.value.delete(dialogId);
      dialogsMessagesTimestamps.value.delete(dialogId);
    }

    const cacheUnsentMessage = ({ dialogId, message }) => {
      if (cachedDialogsMessages.value.has(dialogId) && message.length !== 0) {
        cachedDialogsMessages.value.get(dialogId).unsentMessage = message;
      } else {
        delete cachedDialogsMessages.value.get(dialogId)?.unsentMessage;
      }
    };

    const getCachedUnsentMessage = (dialogId) =>
      cachedDialogsMessages.value.get(dialogId)?.unsentMessage;

    const messages = computed(() => {
      const dialogId = dialogsStore.activeDialog?.id;
      if (!dialogId) return [];

      const cachedMessages =
        cachedDialogsMessages.value.get(dialogId)?.messages || [];
      const uncachedMessages =
        uncachedDialogsMessages.value.get(dialogId)?.messages || [];
      const hasMore = uncachedDialogsMessages.value.get(dialogId)?.hasMore;

      const allMessages = [...cachedMessages, ...uncachedMessages];

      // If we've reached the end of the conversation, add security message
      if (hasMore === false) {
        const securityMessage = {
          id: `security_message_${dialogId}`,
          message: "",
          isSecurityMessage: true,
          created_at: new Date(0).toISOString(),
          user_id_sender: null,
          loading: false,
        };
        allMessages.push(securityMessage);
      }

      return allMessages;
    });

    return {
      // State
      handlerInitialized,
      newMessage,
      cachedDialogsMessages,
      uncachedDialogsMessages,
      dialogsMessagesTimestamps,

      // Getters
      messages,

      // Actions
      setNewMessage,
      setHandlerInitialized,
      addMessage,
      clearMessages,
      fetchMessages,
      fetchMoreMessages,
      cacheUnsentMessage,
      getCachedUnsentMessage,
      removeMessage,
    };
  },
  {
    persist: {
      paths: ["cachedDialogsMessages", "dialogsMessagesTimestamps"],
      beforeRestore(context) {
        context.store.cachedDialogsMessages = new Map();
        context.store.dialogsMessagesTimestamps = new Map();
      },
      serializer: {
        deserialize(value) {
          return JSON.parse(value, (key, value) => {
            if (typeof value === "object" && value !== null && value.isMap)
              return new Map(value.value);
            return value;
          });
        },
        serialize(state) {
          return JSON.stringify(state, (key, value) => {
            if (value instanceof Map) {
              return { isMap: true, value: Array.from(value.entries()) };
            }
            return value;
          });
        },
      },
    },
  }
);
